import _ from 'lodash'
import eventCategories from '@/constant/eventCategories'
import util from '@/util'
import { isFocusingOnAnInput } from '@/utils/dom.utils'
import { generateBlockHash } from '@/utils/CanvasBlocksLayout'

export default {
  computed: {
    isSelectingTextInBlock() {
      return window.getSelection().toString()
    },
    isSelectingBlockOrGroupBlocks() {
      return this.selectingBlocks && this.selectingBlocks.length > 0 && !this.isSelectingTextInBlock
    },
    isDeleteBlockEnabled() {
      return this.isMultipleBlocksSelecting ||
      (this.selectingBlocks && this.selectingBlocks.length > 0 &&
        !this.editingBlockId
      )
    },
    isPasteBlockEnabled() {
      return !isFocusingOnAnInput() && !this.isUploadImageModalShowing &&
        this.$store.state.clipboardBlocks && this.$store.state.clipboardBlocks.length > 0 &&
        !this.editingBlockId
    },
    isChangeBlockOrderDisabled() {
      return !this.selectingBlocks || this.selectingBlocks.length > 1 ||
      this.selectingBlocks.length === 0
    },
  },
  methods: {
    listenBlockShortcutKeys() {
      // Ctrl-A: Select all blocks
      window.addEventListener('keydown', this.onEventSelectAllBlocks)
      // Ctrl-X: Cut blocks
      window.addEventListener('keydown', this.onEventCutBlocks)
      // Ctrl-C: Copy the block when: active a block
      window.addEventListener('keydown', this.onEventCopyBlock)
      // Ctrl-V: Only paste block when not editing any blocks: this.editingBlockId == null
      window.addEventListener('keydown', this.onEventPasteBlock)
      // Ctrl-D: Only paste block when not editing any blocks: this.editingBlockId == null
      window.addEventListener('keydown', this.onEventDuplicateBlock)
      // Delete/Backspace: Only delete block if we're not focusing on the editor
      window.addEventListener('keyup', this.onEventDeleteBlock)
      // Holding Shift/Ctrl
      window.addEventListener('keydown', this.onMetaKeysDown)
      window.addEventListener('keyup', this.onMetaKeysUp)
      // Press Arrow Direction to move block
      window.addEventListener('keydown', this.onEventMoveBlock)
      // Ctrl-Shift-F: Bring to Front; Ctrl-Shift-Alt-F: Bring Forward
      // Ctrl-Shift-B: Send to Back; Ctrl-Shift-Alt-B: Send backward
      window.addEventListener('keydown', this.onEventChangeBlockOrder)
    },
    unListenBlockShortcutKeys() {
      window.removeEventListener('keydown', this.onEventSelectAllBlocks)
      window.removeEventListener('keydown', this.onEventCutBlocks)
      window.removeEventListener('keydown', this.onEventDuplicateBlock)
      window.removeEventListener('keydown', this.onEventPasteBlock)
      window.removeEventListener('keydown', this.onEventCopyBlock)
      window.removeEventListener('keyup', this.onEventDeleteBlock)
      window.removeEventListener('keydown', this.onMetaKeysDown)
      window.removeEventListener('keydown', this.onEventMoveBlock)
      window.removeEventListener('keyup', this.onMetaKeysUp)
      window.removeEventListener('keydown', this.onEventChangeBlockOrder)
    },

    isFocusingOnCanvas() {
      return document.activeElement.nodeName === 'BODY'
    },

    onMetaKeysDown(e) {
      if (!this.isFocusingOnCanvas()) return
      if (e.key === 'Shift') {
        this.holdingShiftKey = true
      }

      if ((e.key === 'Control' || e.key === 'Meta') && !isFocusingOnAnInput()) {
        this.holdingCtrlKey = true
      }
    },
    onMetaKeysUp(e) {
      if (!this.isFocusingOnCanvas()) return
      if (e.key === 'Shift') {
        this.holdingShiftKey = false
      }
      if (e.key === 'Control' || e.key === 'Meta') {
        this.holdingCtrlKey = false
      }
    },

    onEventPasteBlock(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (this.isPasteBlockEnabled) {
          e.preventDefault()
          e.stopPropagation()
          this.pasteBlock('Ctrl-V')
        }
      }
    },

    pasteBlock(pasteMethod) {
      if (!this.isPasteBlockEnabled) return
      const blocksToPaste = JSON.parse(JSON.stringify(this.$store.state.clipboardBlocks))
      this.onPasteBlocks(blocksToPaste)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Duplicate canvas blocks', pasteMethod, this.slideActive.id, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
      })
    },

    onEventChangeBlockOrder(e) {
      if ((e.ctrlKey || e.metaKey)) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          if (this.isChangeBlockOrderDisabled) return
          if (e.shiftKey) {
            this.sendToBack(this.selectingBlocks[0], 'Ctrl-Shift-Down')
          } else {
            this.sendBackward(this.selectingBlocks[0], 'Ctrl-Down')
          }
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault()
          if (this.isChangeBlockOrderDisabled) return
          if (e.shiftKey) {
            this.bringToFront(this.selectingBlocks[0], 'Ctrl-Shift-Up')
          } else {
            this.bringForward(this.selectingBlocks[0], 'Ctrl-Up')
          }
        }
      }
    },

    onEventCopyBlock(e) {
      if (((e.ctrlKey || e.metaKey) && e.key === 'c')) {
        this.copyBlocks('Ctrl-C')
      }
    },

    copyBlocks(copyMethod) {
      if (!this.isSelectingBlockOrGroupBlocks) return
      this.$store.state.clipboardBlocks = [...this.selectingBlocks]
      this.$store.state.clipboardSlideId = this.slideActive.id
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Copy canvas blocks', copyMethod, null, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
        slideId: this.slideActive.id,
      })
    },

    onEventDuplicateBlock(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        this.duplicateBlocks('Ctrl-D')
      }
    },

    duplicateBlocks(duplicateMethod) {
      if (!this.isSelectingBlockOrGroupBlocks) return
      const blocksToCopy = JSON.parse(JSON.stringify(this.selectingBlocks))
      this.onDuplicateBlocks(blocksToCopy)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Duplicate canvas blocks', duplicateMethod, null, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
        slideId: this.slideActive.id,
      })
    },

    onEventSelectAllBlocks(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        const selectionText = window.getSelection().toString()
        if (!selectionText && !isFocusingOnAnInput()) {
          e.preventDefault()
          this.$store.state.selectingBlocks = this.blocksArray ? this.blocksArray.filter((b) => !b.hidden) : []
          util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Select canvas blocks', 'Ctrl-A', this.slideActive.id)
        }
      }
    },
    onEventCutBlocks(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault()
        this.cutBlocks('Ctrl-X')
      }
    },

    cutBlocks(cutMethod) {
      if (!this.isSelectingBlockOrGroupBlocks) return
      this.$store.state.clipboardBlocks = [...this.selectingBlocks]
      this.$store.state.clipboardSlideId = this.slideActive.id
      this.onDeleteBlocks(this.selectingBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Cut canvas blocks', cutMethod, null, {
        slideId: this.slideActive.id,
      })
    },

    onEventDeleteBlock(e) {
      if ((e.key === 'Backspace' || e.key === 'Delete')) {
        this.deleteBlocks('Delete key')
      }
    },

    deleteBlocks(deleteMethod) {
      if (!this.isDeleteBlockEnabled) return
      this.onDeleteBlocks(this.selectingBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Delete canvas blocks', deleteMethod, null, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
        slideId: this.slideActive.id,
      })
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    onEventMoveBlock: _.throttle(function throttleMoveBlock(e) {
      const directionKeys = ['ArrowRight', 'ArrowUp', 'ArrowDown', 'ArrowLeft']
      if (directionKeys.includes(e.key) &&
        this.selectingBlocks &&
        this.selectingBlocks.length > 0 &&
        !this.editingBlockId &&
        !this.holdingCtrlKey &&
        !this.holdingShiftKey
      ) {
        const currentSelectingBlocks = this.selectingBlocks
        const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
        const newCanvasBlocks = { ...oldCanvasBlocks }
        this.selectingBlocks.forEach((block) => {
          const oldBlock = this.dataCanvasBlocks[block.id]
          const currentTransform = oldBlock.style.transform
          // eslint-disable-next-line max-len
          const currentTranslate = currentTransform.matchAll(/translate\((?<x>[+-]?([0-9]*[.])?[0-9]+)px,\s?(?<y>[+-]?([0-9]*[.])?[0-9]+)px\)/g)
          const translateCoords = {}
          let translateStr = ''
          // eslint-disable-next-line no-restricted-syntax
          for (const match of currentTranslate) {
            // eslint-disable-next-line prefer-destructuring
            translateStr = match[0]
            if (match.groups && match.groups.x && match.groups.y) {
              translateCoords.x = match.groups.x
              translateCoords.y = match.groups.y
            }
            break
          }
          if (!translateStr) return
          let newTransform = currentTransform
          const moveDelta = this.holdingShiftKey ? 20 : 2
          if (e.key === 'ArrowRight') {
            newTransform = currentTransform.replace(translateStr, `translate(${+translateCoords.x + moveDelta}px, ${+translateCoords.y}px)`)
          } else if (e.key === 'ArrowUp') {
            newTransform = currentTransform.replace(translateStr, `translate(${+translateCoords.x}px, ${+translateCoords.y - moveDelta}px)`)
          } else if (e.key === 'ArrowLeft') {
            newTransform = currentTransform.replace(translateStr, `translate(${+translateCoords.x - moveDelta}px, ${+translateCoords.y}px)`)
          } else if (e.key === 'ArrowDown') {
            newTransform = currentTransform.replace(translateStr, `translate(${+translateCoords.x}px, ${+translateCoords.y + moveDelta}px)`)
          }

          newCanvasBlocks[block.id] = {
            ...newCanvasBlocks[block.id],
            style: {
              ...newCanvasBlocks[block.id].style,
              transform: newTransform,
            },
            h: generateBlockHash(),
          }
        })

        this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
        this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, currentSelectingBlocks)

        this.$nextTick(() => {
          this.key = Date.now()
        })
      }
    }, 100),
  },
}
